import AppCard from '@nxsys/components/AppCard';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { NmwAgeBandField } from '@nxsys/types/models/kid/umbrella-rate-calculator';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Spin } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNmwRatesByTaxYear } from './useNmwRatesByTaxYear';

/** Irish tax years are calendar years: current year and the two before it */
const getIrelandTaxYearOptions = (): Option<string>[] => {
  const currentYear = new Date().getFullYear();
  return [0, 1, 2].map((offset) => {
    const year = currentYear - offset;
    return {
      label: `${offset ? 'Last' : 'Current'} Tax Year [${year}]`,
      value: `${year}`,
    };
  });
};

const RevenueNMWRates: React.FC = () => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const taxYearOptions = useMemo(() => getIrelandTaxYearOptions(), []);
  const [selectedTaxYear, setSelectedTaxYear] = useState<Option<string>>(
    taxYearOptions[0],
  );
  // NX-5971: rates come from the backend table the payroll NMW check uses
  const {
    fields: data,
    loading,
    error,
  } = useNmwRatesByTaxYear(
    selectedTaxYear?.value ? Number(selectedTaxYear.value) : null,
  );

  const columns: ColumnsType<NmwAgeBandField> = [
    {
      title: messages['common.ageGroup'],
      dataIndex: 'label',
      key: 'label',
      width: 200,
    },
    {
      title: messages['common.minimumHourlyRateOfPay'],
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (value: number) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  return (
    <AppCard
      title={
        <div className="flex items-center justify-between">
          <div>{messages['common.nmwRates']}</div>
          <div>
            <AppSelect
              options={taxYearOptions}
              onChange={(value: Option<string>) => setSelectedTaxYear(value)}
              defaultValue={taxYearOptions[0]}
            />
          </div>
        </div>
      }
      lineTitleColor="#FFE785"
    >
      <Spin spinning={loading}>
        {error && (
          <div className="mb-2 text-red-500">
            {messages['common.somethingWentWrong']}
          </div>
        )}
        <AppTableContainer
          rowKey="key"
          columns={columns}
          data={data}
          loading={false}
          pagination={false}
          page={1}
          total={data.length}
          pageSize={data.length}
          scroll={{ x: 400 }}
        />
      </Spin>
    </AppCard>
  );
};

export default RevenueNMWRates;
